import { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Badge, { PosBadge } from '../components/Badge.jsx';
import DistributorForm from '../components/DistributorForm.jsx';
import ContactsSection from '../components/ContactsSection.jsx';
import SendEmailDialog from '../components/SendEmailDialog.jsx';

// Distributor detail page. Layout mirrors LeadDetail.jsx (Dave's
// approved 2026-05-31): record header with Send Email + Delete
// actions, tab strip (Details / Notes / Documents), then the flat
// contact form + Distributor Details section + Contacts section.
//
// Differences from leads:
//   - No Package selector (distributors don't carry a package).
//   - No Generate Quote (no quotes for referral partners).
//   - No Convert-to-Customer (distributors aren't a sales pipeline stage).

const TABS = [
  { id: 'details',   label: 'Details' },
  { id: 'notes',     label: 'Notes' },
  { id: 'documents', label: 'Documents' }
];

function displayName(d) {
  if (d?.company) return d.company;
  const n = [d?.first_name, d?.last_name].filter(Boolean).join(' ');
  return n || `Distributor #${d?.id ?? ''}`;
}

export default function DistributorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [active, setActive] = useState('details');
  const [saveMessage, setSaveMessage] = useState(null);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  // Sub-contact email send (Dave 2026-06-13): when Zoe clicks Send
  // Email on a contact row (e.g. Jane Smith at Guardian Fuel Tech),
  // we open the same SendEmailDialog but with a synthesized record
  // that uses the sub-contact's name + email. company / position
  // / outreach context still come from the parent distributor so
  // template merge tokens make sense.
  const [emailSubContact, setEmailSubContact] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/distributors/${id}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function handleUpdate(form) {
    await axios.put(`/api/distributors/${id}`, form);
    setSaveMessage('Saved.');
    await load();
    setTimeout(() => setSaveMessage(null), 2500);
  }

  async function handleDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await axios.delete(`/api/distributors/${id}`);
      navigate('/distributors');
    } catch (err) {
      setDeleteError(err.response?.data?.error || err.message);
      setDeleting(false);
    }
  }

  if (loading && !data) {
    return <section className="page"><p>Loading…</p></section>;
  }
  if (error && !data) {
    return (
      <section className="page">
        <div className="page-header">
          <h2>Distributor</h2>
          <Link to="/distributors" className="btn-secondary">← Back to Distributors</Link>
        </div>
        <p className="error">{error}</p>
      </section>
    );
  }

  const dist = data.distributor;
  const location = [dist.city, dist.state || dist.province_state].filter(Boolean).join(', ');
  const contactName = [dist.first_name, dist.last_name].filter(Boolean).join(' ');

  return (
    <section className="page">
      <div className="page-header">
        <Link to="/distributors" className="btn-secondary">← Back to Distributors</Link>
      </div>

      <div className="record-header">
        <div className="record-title" style={{ flex: 1 }}>
          <h2>{displayName(dist)}</h2>
          <div className="record-meta">
            {dist.pos_type && <PosBadge value={dist.pos_type} />}
            {dist.current_status && <><span className="sep" /><Badge tone="teal">{dist.current_status}</Badge></>}
            {dist.territory && <><span className="sep" />{dist.territory}</>}
            {location && <><span className="sep" />{location}</>}
            {contactName && <><span className="sep" />{contactName}</>}
          </div>
        </div>
        <div className="record-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setSendEmailOpen(true)}
            disabled={deleting}
            title="Send a templated email to this distributor"
          >
            Send Email
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => setDeleteConfirmOpen(true)}
            disabled={deleting}
            title="Delete this distributor and all related records"
          >
            Delete
          </button>
        </div>
      </div>

      <SendEmailDialog
        open={sendEmailOpen}
        record={dist}
        recordType="distributor"
        onClose={() => setSendEmailOpen(false)}
        onSent={() => { load(); }}
      />

      {/* Sub-contact email dialog. Synthesizes a "record" that looks
          like a distributor to SendEmailDialog -- preserves company /
          position / etc. from the parent distributor, overrides
          first_name / last_name / email1 with the sub-contact's so the
          merge tokens render the SUB-contact's name and the To field
          defaults to their address. Dave 2026-06-13. */}
      {emailSubContact && (
        <SendEmailDialog
          open={!!emailSubContact}
          record={{
            ...dist,
            first_name: emailSubContact.first_name || emailSubContact.name || '',
            last_name:  emailSubContact.last_name  || '',
            position:   emailSubContact.position   || dist.position,
            phone_main: emailSubContact.phone1     || dist.phone_main,
            email1:     emailSubContact.email      || '',
            email2:     null,
            email3:     null,
            // sub_contact id for downstream tagging if we ever want
            // to attach the send to the specific sub-contact row.
            sub_contact_id: emailSubContact.id
          }}
          recordType="distributor"
          onClose={() => setEmailSubContact(null)}
          onSent={() => { load(); setEmailSubContact(null); }}
        />
      )}

      {/* Destructive-delete confirmation - matches the lead delete modal. */}
      {deleteConfirmOpen && (
        <div
          className="modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !deleting) setDeleteConfirmOpen(false);
          }}
        >
          <div className="modal-card modal-narrow" role="dialog" aria-modal="true" aria-label="Delete distributor">
            <header className="modal-head">
              <h3>Delete Distributor</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleting}
                aria-label="Close"
              >×</button>
            </header>
            <div className="modal-body">
              <p>Are you sure you want to delete this distributor? This cannot be undone.</p>
              <p className="modal-sub">
                Deleting <strong>{displayName(dist)}</strong> will remove the
                contact record plus all related notes, contacts, and
                documents (cascade).
              </p>
              {deleteError && <p className="error">{deleteError}</p>}
            </div>
            <footer className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete distributor'}
              </button>
            </footer>
          </div>
        </div>
      )}

      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`tab${active === t.id ? ' tab-active' : ''}`}
            onClick={() => setActive(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="tab-panel">
        {saveMessage && <p className="save-message">{saveMessage}</p>}

        {active === 'details' && (
          <>
            <DistributorForm
              key={dist.id}
              initial={dist}
              onSubmit={handleUpdate}
              submitLabel="Save Changes"
            />
            {/* Contacts panel matches the customer/lead pattern -
                same component, sub_contacts table, primary toggle. */}
            <ContactsSection
              contactId={dist.id}
              items={dist.sub_contacts || []}
              onChanged={load}
              onSendEmail={(subContact) => setEmailSubContact(subContact)}
            />
          </>
        )}

        {active === 'notes' && (
          <p className="panel-empty">Notes tab — coming soon.</p>
        )}

        {active === 'documents' && (
          <p className="panel-empty">Documents tab — coming soon.</p>
        )}
      </div>
    </section>
  );
}
